import React, { useState, useEffect, useMemo, memo, useRef } from 'react';
import { Link } from "react-router-dom";
import 'bootstrap/dist/css/bootstrap.min.css';
import { usePlantData } from '../../../components/data/PlantData';
import SidebarPageLayout from '../../../components/layout/SidebarPageLayout';
import Marquee from '../../../components/layout/Marquee';
import Breadcrumbs from '../../../components/layout/Breadcrumbs';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChartColumn, faInfoCircle, faBatteryFull, faTableColumns, faTable } from '@fortawesome/free-solid-svg-icons';
import PowerblockAvailability from '../../../components/layout/PowerblockAvailability';
import { apiFetch } from '../../../api/apiClient';
import { toSlug } from '../../../util/strings'; 
import Tooltip from '../../../components/layout/Tooltip';
import inverterIcon from '../../../img/inverter-icon.svg';
import dcdcConnverterIcon from '../../../img/dcdc-converter-icon.svg';
import TimescaleTabs, { FORECAST_TIMESCALE_TABS } from '../../../components/layout/TimescaleTabs';
import ChartTooltip from '../../../components/layout/ChartTooltip';
import MultiLineChart from '../../../components/graphs/MultiLineChart';
import DataTable from 'datatables.net-bs5';
import 'datatables.net-bs5/css/dataTables.bootstrap5.min.css';
import { usePortfolioDateFormatter } from '../../../utils/usePortfolioDateFormatter';
import { parseUtcDateString } from '../../../utils/dateTime';

const PAGE_LENGTH_OPTIONS = [20, 50, 100, -1];
const PAGE_LENGTH_LABELS = [20, 50, 100, 'All'];

const TRANSFORMER_IDS = ['1', '2', '3', '4'];

const ONLINE_RESOURCE_TIMESCALES = [
  { duration: '24hr', key: '24hr' },
  { duration: '48hr', key: '48hr' },
  { duration: null, key: 'week' },
];

const DEFAULT_CAPACITIES = { inverters: 186, converters: 858, batteries: 4290 };

const buildOnlineDeviceCharts = (capacities) => {
  const inv = capacities?.inverters ?? DEFAULT_CAPACITIES.inverters;
  const conv = capacities?.converters ?? DEFAULT_CAPACITIES.converters;
  const batt = capacities?.batteries ?? DEFAULT_CAPACITIES.batteries;
  const mid = (n) => Math.round(n / 2);

  return [
    {
      key: 'inverters',
      capacity: inv,
      summaryLabel: 'ONLINE INVERTERS',
      columnLabel: 'Online Inverters',
      domain: Object.freeze([0, inv]),
      domainWeek: Object.freeze([-1, inv]),
      tickValues: Object.freeze([inv, 0]),
      midlines: Object.freeze([{ value: mid(inv), label: String(mid(inv)) }]),
      wrapperClassName: 'online-device-chart online-inverters mt-3',
      headerLeft: <strong>INVERTERS</strong>,
      headerRight: <strong>Online Inverters</strong>,
    },
    {
      key: 'converters',
      capacity: conv,
      summaryLabel: 'ONLINE DCDC',
      columnLabel: 'Online DCDC Converters',
      domain: Object.freeze([0, conv]),
      domainWeek: Object.freeze([-1, conv]),
      tickValues: Object.freeze([conv, 0]),
      midlines: Object.freeze([{ value: mid(conv), label: String(mid(conv)) }]),
      wrapperClassName: 'online-device-chart online-converters mt-3',
      headerLeft: <strong>DCDC CONVERTERS</strong>,
      headerRight: <strong>Online DCDC Converters</strong>,
    },
    {
      key: 'batteries',
      capacity: batt,
      summaryLabel: 'ONLINE BATTERY RACKS',
      columnLabel: 'Online Battery Racks',
      domain: Object.freeze([0, batt]),
      domainWeek: Object.freeze([-1, batt]),
      tickValues: Object.freeze([batt, 0]),
      midlines: Object.freeze([{ value: mid(batt), label: String(mid(batt)) }]),
      wrapperClassName: 'online-device-chart online-batteries mt-3',
      headerLeft: <strong>BATTERY RACKS</strong>,
      headerRight: <strong>Online Battery Racks</strong>,
    },
  ];
};

const ONLINE_DEVICE_LINE_COLOR = '#008900';

const OnlineDeviceChart = memo(function OnlineDeviceChart({ chart, duration, data, onTooltip, startDate, endDate, timeZone }) {
  return (
    <MultiLineChart
      duration={duration}
      startDate={startDate}
      endDate={endDate}
      color={ONLINE_DEVICE_LINE_COLOR}
      domain={duration ? chart.domain : chart.domainWeek}
      tickValues={chart.tickValues}
      midlines={chart.midlines}
      unit=""
      tooltipUnit="online"
      showPoints
      wrapperClassName={chart.wrapperClassName}
      headerLeft={chart.headerLeft}
      headerRight={chart.headerRight}
      data={data}
      onTooltip={onTooltip}
      timeZone={timeZone}
    />
  );
});

const capacitiesFromPlantKpi = (plant) => {
  const invTotal = (Number(plant?.inverters_online) || 0) + (Number(plant?.inverters_offline) || 0);
  const convTotal = (Number(plant?.dcdc_converters_online) || 0) + (Number(plant?.dcdc_converters_offline) || 0);
  const battTotal = (Number(plant?.battery_racks_online) || 0) + (Number(plant?.battery_racks_offline) || 0);
  return {
    inverters: invTotal || DEFAULT_CAPACITIES.inverters,
    converters: convTotal || DEFAULT_CAPACITIES.converters,
    batteries: battTotal || DEFAULT_CAPACITIES.batteries,
  };
};

const isPlantWideXfmr = (selectedXfmr) =>
  selectedXfmr == null || selectedXfmr === '' || selectedXfmr === 'all' || selectedXfmr === '0';

const pairOnlineOffline = (online, offline) => `${online ?? 0} / ${offline ?? 0}`;

const comparePowerblockIds = (a, b) => {
  const parse = (id) => {
    const match = String(id).match(/^([A-Za-z]*)(\d*)$/);
    return { prefix: match?.[1] ?? String(id), number: match?.[2] ? Number(match[2]) : 0 };
  };
  const pa = parse(a);
  const pb = parse(b);
  if (pa.prefix !== pb.prefix) {
    return pa.prefix.localeCompare(pb.prefix);
  }
  return pa.number - pb.number;
};

const parseOnlineResourceSeries = (series) =>
  (Array.isArray(series) ? series : [])
    .map((point) => {
      const date = point?.date != null ? new Date(point.date) : null;
      const value = point?.value != null ? Number(point.value) : NaN;
      return { date, value };
    })
    .filter((point) => point.date instanceof Date && !Number.isNaN(point.date.getTime()) && !Number.isNaN(point.value));

const formatOnlineResourceTimestamp = (date, timescaleKey, dateFormatter) => {
  if (timescaleKey === 'week') {
    return dateFormatter.formatDate(date, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  }

  return dateFormatter.formatDate(date, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
};

const summarizeTimelineValues = (points) => {
  if (!points.length) {
    return { max: '—', min: '—', avg: '—' };
  }

  const values = points.map((point) => point.value);
  const max = Math.max(...values);
  const min = Math.min(...values);
  const avg = Math.round(values.reduce((sum, value) => sum + value, 0) / values.length);

  return { max, min, avg };
};

function destroyDataTable(dataTableRef, restore = true) {
  if (!dataTableRef.current) {
    return;
  }

  try {
    dataTableRef.current.destroy(restore);
  } catch (error) {
    console.warn('Failed to destroy DataTable instance:', error);
  } finally {
    dataTableRef.current = null;
  }
}

function resetTableHost(tableHostRef, dataTableRef) {
  destroyDataTable(dataTableRef);

  if (tableHostRef.current) {
    tableHostRef.current.innerHTML = '';
  }
}

function formatTableInfo(_settings, start, end, _max, total) {
  if (total === 0) {
    return 'Showing 0 to 0 out of 0 entries';
  }

  return `Showing ${start} to ${end} out of ${total} entries`;
}

const buildOnlineResourcesTableColumns = (charts) => [
  {
    title: 'Timestamp',
    render: (_cellData, type, row) => {
      if (type === 'sort' || type === 'type') {
        return row[0];
      }
      return row[1];
    },
  },
  ...charts.map((chart, index) => ({
    title: chart.columnLabel,
    render: (_cellData, type, row) => {
      const value = row[index + 2];
      if (type === 'sort' || type === 'type') {
        const num = Number(value);
        return Number.isNaN(num) ? 0 : num;
      }
      return value;
    },
  })),
];

const compareByAvailability = (a, b, data) => {
  const rankA = data[a]?.availability_sort_rank ?? 4;
  const rankB = data[b]?.availability_sort_rank ?? 4;
  if (rankA !== rankB) {
    return rankA - rankB;
  }
  return comparePowerblockIds(a, b);
};

function Availability() {
  const { plant: gemini } = usePlantData();
  const dateFormatter = usePortfolioDateFormatter();
  const portfolioName = toSlug(gemini.plant_name) ?? '';
  const [selectedXfmr, setSelectedXfmr] = useState('1');
  const [selectedSort, setSelectedSort] = useState('sort-id');
  const [data, setData] = useState(null);
  const [onlineResourcesTab, setOnlineResourcesTab] = useState(0);
  const [onlineResourcesChartView, setOnlineResourcesChartView] = useState(true);
  const [onlineResourcesData, setOnlineResourcesData] = useState(null);
  const [onlineResourcesLoading, setOnlineResourcesLoading] = useState(false);
  const [tooltip, setTooltip] = useState({ visible: false, x: 0, y: 0, content: '' });
  const onlineResourcesTableHostRef = useRef(null);
  const onlineResourcesDataTableRef = useRef(null);

  useEffect(() => {
    document.title = "Vivosol Plant Performance Insights Hub | Gemini / Insights / Availability";
  }, []);

  useEffect(() => {
    if (!portfolioName) {
      return undefined;
    }

    let cancelled = false;

    (async () => {
      try {
        const transformerParam = encodeURIComponent(selectedXfmr);
        const result = await apiFetch(
          `/api/devices/portfolio/${portfolioName}/powerblock-availability?transformerId=${transformerParam}`
        );

        if (!cancelled) {
          setData(result && typeof result === 'object' && !result.error ? result : null);
        }
      } catch (apiError) {
        console.warn(
          `API fetch failed for /api/devices/portfolio/${portfolioName}/powerblock-availability (xfmr ${selectedXfmr}):`,
          apiError
        );
        if (!cancelled) {
          setData(null);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [selectedXfmr, portfolioName]);

  const activeOnlineTimescale = ONLINE_RESOURCE_TIMESCALES[onlineResourcesTab]
    ?? ONLINE_RESOURCE_TIMESCALES[0];

  useEffect(() => {
    if (!portfolioName) {
      return undefined;
    }

    let cancelled = false;
    const duration = activeOnlineTimescale.key;

    setOnlineResourcesLoading(true);

    (async () => {
      try {
        const transformerParam = encodeURIComponent(selectedXfmr);
        const raw = await apiFetch(
          `/api/graph/portfolio/${portfolioName}/insights/availability/online-resources/${duration}?transformerId=${transformerParam}`
        );
        if (!cancelled) {
          setOnlineResourcesData({
            capacities: raw.capacities ?? raw.summary?.capacities ?? null,
            summary: raw.summary ?? null,
            inverters: parseOnlineResourceSeries(raw.inverters),
            converters: parseOnlineResourceSeries(raw.converters),
            batteries: parseOnlineResourceSeries(raw.batteries),
          });
        }
      } catch (apiError) {
        console.warn(
          `API fetch failed for /api/graph/portfolio/${portfolioName}/insights/availability/online-resources/${duration} (xfmr ${selectedXfmr}):`,
          apiError
        );
        if (!cancelled) {
          setOnlineResourcesData(null);
        }
      } finally {
        if (!cancelled) {
          setOnlineResourcesLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [activeOnlineTimescale.key, portfolioName, selectedXfmr]);

  const chartCapacities = useMemo(() => {
    // Plant-wide charts use site inventory (858 / 4290), not inferred PB caps.
    if (isPlantWideXfmr(selectedXfmr)) {
      return capacitiesFromPlantKpi(gemini);
    }
    return onlineResourcesData?.capacities ?? capacitiesFromPlantKpi(gemini);
  }, [onlineResourcesData?.capacities, gemini, selectedXfmr]);

  const onlineDeviceCharts = useMemo(
    () => buildOnlineDeviceCharts(chartCapacities),
    [chartCapacities]
  );

  const onlineResourcesTableColumns = useMemo(
    () => buildOnlineResourcesTableColumns(onlineDeviceCharts),
    [onlineDeviceCharts]
  );

  const sidebarTotals = useMemo(() => {
    // Plant-wide: always KPI inventory so online + offline = site totals.
    if (isPlantWideXfmr(selectedXfmr)) {
      return {
        inverters: pairOnlineOffline(gemini.inverters_online, gemini.inverters_offline),
        dcdcConverters: pairOnlineOffline(gemini.dcdc_converters_online, gemini.dcdc_converters_offline),
        batteries: pairOnlineOffline(gemini.battery_racks_online, gemini.battery_racks_offline),
      };
    }

    const summary = onlineResourcesData?.summary;
    if (summary) {
      return {
        inverters: pairOnlineOffline(summary.online_inverters, summary.offline_inverters),
        dcdcConverters: pairOnlineOffline(summary.online_dcdc_converters, summary.offline_dcdc_converters),
        batteries: pairOnlineOffline(summary.online_battery_racks, summary.offline_battery_racks),
      };
    }

    return {
      inverters: pairOnlineOffline(gemini.inverters_online, gemini.inverters_offline),
      dcdcConverters: pairOnlineOffline(gemini.dcdc_converters_online, gemini.dcdc_converters_offline),
      batteries: pairOnlineOffline(gemini.battery_racks_online, gemini.battery_racks_offline),
    };
  }, [onlineResourcesData?.summary, gemini, selectedXfmr]);

  const allPowerblockIds = useMemo(
    () => (data ? Object.keys(data) : []),
    [data]
  );

  const sortedPowerblockIds = useMemo(() => {
    if (allPowerblockIds.length === 0) {
      return [];
    }

    const ids = [...allPowerblockIds];
    if (selectedSort === 'sort-availability') {
      ids.sort((a, b) => compareByAvailability(a, b, data));
    } else {
      ids.sort(comparePowerblockIds);
    }
    return ids;
  }, [allPowerblockIds, selectedSort, data]);

  // Snapshot time for the PB availability grid (MAX LAST_UPDATED in the loaded set).
  const availabilitySnapshotLabel = useMemo(() => {
    if (!data || typeof data !== 'object') {
      return null;
    }
    let latestMs = null;
    for (const pb of Object.values(data)) {
      if (pb?.last_updated == null || pb.last_updated === '') {
        continue;
      }
      const parsed = parseUtcDateString(pb.last_updated);
      if (!parsed) {
        continue;
      }
      if (latestMs == null || parsed.getTime() > latestMs) {
        latestMs = parsed.getTime();
      }
    }
    if (latestMs == null) {
      return null;
    }
    return dateFormatter.formatUtc(new Date(latestMs));
  }, [data, dateFormatter]);

  const onlineDeviceTimelineData = useMemo(() => {
    if (!onlineResourcesData) {
      return { inverters: [], converters: [], batteries: [] };
    }
    return {
      inverters: onlineResourcesData.inverters ?? [],
      converters: onlineResourcesData.converters ?? [],
      batteries: onlineResourcesData.batteries ?? [],
    };
  }, [onlineResourcesData]);

  const onlineResourcesMetrics = useMemo(
    () => Object.fromEntries(
      onlineDeviceCharts.map((chart) => [
        chart.key,
        summarizeTimelineValues(onlineDeviceTimelineData[chart.key] ?? []),
      ])
    ),
    [onlineDeviceTimelineData, onlineDeviceCharts]
  );

  const onlineResourcesTableRows = useMemo(() => {
    const timestampSet = new Set();
    onlineDeviceCharts.forEach((chart) => {
      (onlineDeviceTimelineData[chart.key] ?? []).forEach((point) => {
        timestampSet.add(point.date.getTime());
      });
    });

    return [...timestampSet]
      .sort((a, b) => a - b)
      .map((ts) => {
        const date = new Date(ts);
        return {
          date,
          timestamp: formatOnlineResourceTimestamp(date, activeOnlineTimescale.key, dateFormatter),
          values: Object.fromEntries(
            onlineDeviceCharts.map((chart) => {
              const point = (onlineDeviceTimelineData[chart.key] ?? []).find(
                (p) => p.date.getTime() === ts
              );
              return [chart.key, point?.value ?? null];
            })
          ),
        };
      });
  }, [onlineDeviceTimelineData, activeOnlineTimescale.key, onlineDeviceCharts, dateFormatter]);

  const onlineResourcesChartRange = useMemo(() => {
    const anchor = onlineDeviceTimelineData.inverters ?? [];
    if (anchor.length === 0) {
      return { start: null, end: null };
    }
    return {
      start: anchor[0].date,
      end: anchor[anchor.length - 1].date,
    };
  }, [onlineDeviceTimelineData]);

  const onlineResourcesTableData = useMemo(
    () => onlineResourcesTableRows.map((row) => [
      row.date.getTime(),
      row.timestamp,
      ...onlineDeviceCharts.map((chart) => row.values[chart.key]),
    ]),
    [onlineResourcesTableRows, onlineDeviceCharts]
  );

  const toggleOnlineResourcesView = () => {
    setOnlineResourcesChartView((prev) => !prev);
  };

  useEffect(() => {
    if (onlineResourcesChartView) {
      resetTableHost(onlineResourcesTableHostRef, onlineResourcesDataTableRef);
      return undefined;
    }

    const host = onlineResourcesTableHostRef.current;
    if (!host || onlineResourcesTableData.length === 0) {
      return undefined;
    }

    resetTableHost(onlineResourcesTableHostRef, onlineResourcesDataTableRef);

    const table = document.createElement('table');
    table.className = 'table table-striped table-hover online-resources-table w-100';
    table.style.width = '100%';
    host.appendChild(table);

    onlineResourcesDataTableRef.current = new DataTable(table, {
      data: onlineResourcesTableData,
      columns: onlineResourcesTableColumns,
      pageLength: 20,
      lengthMenu: [PAGE_LENGTH_OPTIONS, PAGE_LENGTH_LABELS],
      searching: true,
      paging: true,
      ordering: true,
      order: [[0, 'desc']],
      autoWidth: false,
      initComplete: () => {
        host.querySelectorAll('table.online-resources-table').forEach((el) => {
          el.style.width = '100%';
        });
        host.querySelectorAll('thead th').forEach((th) => {
          th.style.removeProperty('width');
          th.style.removeProperty('min-width');
          th.style.removeProperty('max-width');
        });
        onlineResourcesDataTableRef.current?.columns.adjust().draw(false);
      },
      layout: {
        topStart: 'pageLength',
        topEnd: {
          search: {
            placeholder: 'Search table...',
          },
        },
        bottomStart: {
          info: {
            callback: formatTableInfo,
          },
        },
        bottomEnd: {
          paging: {
            numbers: 5,
          },
        },
      },
    });

    return () => {
      resetTableHost(onlineResourcesTableHostRef, onlineResourcesDataTableRef);
    };
  }, [onlineResourcesChartView, onlineResourcesTableData, onlineResourcesTableColumns]);

  useEffect(() => () => {
    resetTableHost(onlineResourcesTableHostRef, onlineResourcesDataTableRef);
  }, []);

  return (
    <SidebarPageLayout>
      <div className="content p-5">
        <Marquee />
        <ChartTooltip tooltip={tooltip} transition={true} />
        <h1 className="mt-3 mb-0"><Link to={`${gemini.dashboard}`}><strong>{gemini.plant_name}</strong></Link> : {gemini.config} ({gemini.location})</h1>
        <Breadcrumbs labels={{ portfolioName: gemini.plant_name }} />
        <div id="powerblockAvailability" className="panel p-3 mb-5">
          <div className="header d-flex justify-content-between pb-3">
            <div className="left d-flex gap-3 align-items-center">
              <span><FontAwesomeIcon icon={faChartColumn} /> POWERBLOCKS - Availability</span>
              <select
                className="form-select form-select-sm w-auto"
                value={selectedXfmr}
                onChange={(e) => setSelectedXfmr(e.target.value)}
              >
                {TRANSFORMER_IDS.map((id) => (
                  <option key={id} value={id}>XFMR {id}</option>
                ))}
                <option value="all">All Transformers</option>
              </select>
              <select
                className="form-select form-select-sm w-auto"
                value={selectedSort}
                onChange={(e) => setSelectedSort(e.target.value)}
              >
                <option value="sort-id">Sort by ID</option>
                <option value="sort-availability">Sort by Availability</option>
              </select>
            </div>
            <div className="right d-flex align-items-center gap-2">
              {availabilitySnapshotLabel ? (
                <span className="small text-muted text-nowrap" title="Powerblock availability snapshot time">
                  As of {availabilitySnapshotLabel}
                </span>
              ) : (
                <span
                  className="small text-nowrap badge text-bg-secondary"
                  title="No LAST_UPDATED on availability rows — do not treat empty tiles as offline"
                >
                  Stale — no snapshot time
                </span>
              )}
            </div>
          </div>
          <div className={`body gap-5 justify-content-between pt-3 align-items-start d-flex flex-row`}>
            <div className="col-8 powerblock-availability-grid">
              {[0, 1, 2].map((headerIndex) => (
                <div
                  key={`availability-header-${headerIndex}`}
                  className="powerblock-availability powerblock-availability-header"
                  aria-hidden={headerIndex > 0}
                >
                  <div className="powerblock-id">
                    <span>&nbsp;</span>
                  </div>
                  <div className="powerblock-inverters">
                    <span title="Inverters">Inv</span>
                  </div>
                  <div className="powerblock-converters">
                    <span title="DC/DC Converters">DC/DC</span>
                  </div>
                  <div className="powerblock-batteries">
                    <span title="Battery Racks">Batt</span>
                  </div>
                </div>
              ))}
              {data !== null && sortedPowerblockIds.length === 0 && (
                <p className="mb-0 text-start powerblock-availability-empty">No powerblocks available</p>
              )}
              {sortedPowerblockIds.map((id) => {
                const pb = data[id] ?? {};
                // Staleness is row-specific. One fresh PB must not make another
                // PB with no dump timestamp look red / offline.
                const staleSnapshot = pb.last_updated == null || pb.last_updated === '';
                const fallbackStatus = staleSnapshot ? 'unknown' : 'red';
                const inverters = pb.online_inverters ?? (staleSnapshot ? null : 0);
                const inverterStatus = pb.inverter_status ?? fallbackStatus;
                const converters = pb.online_dcdc_converters ?? (staleSnapshot ? null : 0);
                const converterStatus = pb.dcdc_status ?? fallbackStatus;
                const batteries = pb.online_battery_racks ?? (staleSnapshot ? null : 0);
                const batteryStatus = pb.battery_status ?? fallbackStatus;

                return (
                  <PowerblockAvailability
                    key={id}
                    powerblockId={id}
                    inverters={inverters}
                    inverterCapacity={pb.inverter_capacity}
                    inverterStatus={inverterStatus}
                    converters={converters}
                    converterCapacity={pb.dcdc_converter_capacity}
                    converterStatus={converterStatus}
                    batteries={batteries}
                    batteryCapacity={pb.battery_rack_capacity}
                    batteryStatus={batteryStatus}
                    stale={staleSnapshot}
                  />
                );
              })}
            </div>
            <div className="col metrics">
              <h4>Online Resources Today</h4>
              <div><span className="title"><span><img src={inverterIcon} alt="Inverters" />Inverters</span>
                <Tooltip position="left" content="Plant inventory: online vs offline. Totals match site KPI (not powerblock tile caps).">
                  <button type="button"><FontAwesomeIcon icon={faInfoCircle} /></button>
                </Tooltip></span>
              <div>{sidebarTotals.inverters}</div>
              </div>
              <div><span className="title"><span><img src={dcdcConnverterIcon} alt="DCDC Converters" />DCDC Converters</span>
                  <Tooltip position="left" content="Plant inventory online vs offline against the site total (858). Transformer filters use each PB's configured slot capacity.">
                    <button type="button"><FontAwesomeIcon icon={faInfoCircle} /></button>
                  </Tooltip></span>
                <div>{sidebarTotals.dcdcConverters}</div>
              </div>
              <div><span className="title"><span><FontAwesomeIcon icon={faBatteryFull} style={{ transform: 'rotate(-90deg)' }} />Battery Racks</span>
                  <Tooltip position="left" content="Plant inventory online vs offline against the site total (4,290). Transformer filters use each PB's configured rack capacity.">
                    <button type="button"><FontAwesomeIcon icon={faInfoCircle} /></button>
                  </Tooltip></span>
                <div>{sidebarTotals.batteries}</div>
              </div>
            </div>
          </div>
        </div>
        <div id="powerblockOnlineResources" className="panel p-3 mb-5">
          <div className="header d-flex justify-content-between pb-3">
            <div className="left d-flex gap-3 align-items-center">
              <span><FontAwesomeIcon icon={faChartColumn} /> POWERBLOCKS - Online Resources</span>
              <select
                className="form-select form-select-sm w-auto"
                value={selectedXfmr}
                onChange={(e) => setSelectedXfmr(e.target.value)}
                aria-label="Transformer filter"
              >
                {TRANSFORMER_IDS.map((id) => (
                  <option key={id} value={id}>XFMR {id}</option>
                ))}
                <option value="all">All Transformers</option>
              </select>
              <TimescaleTabs
                tabs={FORECAST_TIMESCALE_TABS}
                activeTab={onlineResourcesTab}
                setActiveTab={setOnlineResourcesTab}
              />
            </div>
            <div className="right d-flex align-items-stretch gap-2">
              <button type="button" onClick={toggleOnlineResourcesView}>
                <FontAwesomeIcon icon={onlineResourcesChartView ? faTableColumns : faTable} />
              </button>
            </div>
          </div>
          <div className={`body gap-5 justify-content-between pt-3 align-items-start ${onlineResourcesChartView ? 'd-flex flex-row' : 'd-none'}`}>
            <div className="col-8 powerblock-online-resources">
              {onlineResourcesLoading && (
                <p className="mb-3 text-muted">Loading online resources…</p>
              )}
              {!onlineResourcesLoading && onlineDeviceTimelineData.inverters.length === 0 && (
                <p className="mb-3">No online resources data available</p>
              )}
              {onlineDeviceCharts.map((chart) => (
                <OnlineDeviceChart
                  key={`${chart.key}-${activeOnlineTimescale.key}`}
                  chart={chart}
                  duration={activeOnlineTimescale.duration ?? undefined}
                  startDate={activeOnlineTimescale.key === 'week' ? onlineResourcesChartRange.start : undefined}
                  endDate={activeOnlineTimescale.key === 'week' ? onlineResourcesChartRange.end : undefined}
                  data={onlineDeviceTimelineData[chart.key]}
                  onTooltip={setTooltip}
                  timeZone={dateFormatter.timeZone}
                />
              ))}
            </div>
            <div className="col onlineResourcesMetrics">
              <p className="small text-muted mb-2">
                Over selected window
                {selectedXfmr === 'all' ? ' · All transformers' : ` · XFMR ${selectedXfmr}`}
              </p>
              {onlineDeviceCharts.map((chart) => {
                const stats = onlineResourcesMetrics[chart.key];
                return (
                  <React.Fragment key={chart.key}>
                    <div className="d-flex flex-row align-items-center gap-2 justify-content-between">
                      <strong>{chart.summaryLabel} — MAX</strong>
                      <span>{stats.max}</span>
                    </div>
                    <div className="d-flex flex-row align-items-center gap-2 justify-content-between">
                      <strong>{chart.summaryLabel} — MIN</strong>
                      <span>{stats.min}</span>
                    </div>
                    <div className="d-flex flex-row align-items-center gap-2 justify-content-between">
                      <strong>{chart.summaryLabel} — AVG</strong>
                      <span>{stats.avg}</span>
                    </div>
                  </React.Fragment>
                );
              })}
            </div>
          </div>
          <div className={`body pt-3 ${onlineResourcesChartView ? 'd-none' : 'd-block'}`}>
            {onlineResourcesTableData.length === 0 && (
              <p className="mb-2">No data available</p>
            )}
            <div
              ref={onlineResourcesTableHostRef}
              className="online-resources-table-host"
            />
          </div>
        </div>
      </div>
    </SidebarPageLayout>
  );
}

export default Availability;
